import { useMemo } from "react";
import { RRule } from "rrule";
import invariant from "tiny-invariant";
import { Identifiable } from "@lifeat/shared/data-models/definition";
import { RecurringMasterTask, RecurringOverrideTask } from "@lifeat/shared/task-management/RecurringTaskTypes";
import { calculateDateFromRule } from "@lifeat/shared/task-management/RecurringTaskUtil";
import { CombinedFilter, combineFilters } from "@lifeat/shared/task-management/TaskFilterUtil";
import { isTaskDueToday, isTaskOverdue } from "@lifeat/shared/task-management/TaskManagementUtil";
import {
    getDateIsoString,
    getPlainTodayStart,
    isSamePlainDay,
    localToPlain,
    plainToLocal,
} from "@lifeat/shared/util/DateUtil";
import { useTaskListContext } from "./TaskListContext";
import { ClientRecurringTask } from "./TaskManagementTypes";

type UseRecurringTasksOptions = {
    dateSelectionStrategy: DateSelectionStrategy;
};

export const useRecurringTasks = (options?: UseRecurringTasksOptions) => {
    const { recurringMasters$, recurringOverrides$, filters } = useTaskListContext();

    const recurringTasks = useMemo(() => {
        return assembleTasksFromMastersAndOverrides(
            recurringMasters$.value,
            recurringOverrides$.value,
            options?.dateSelectionStrategy ?? closestToTodayStrategy(),
            combineFilters(filters),
        );
    }, [recurringMasters$.value, recurringOverrides$.value, options?.dateSelectionStrategy, filters]);

    return recurringTasks;
};

export function buildClientRecurringTask(args: {
    master: RecurringMasterTask;
    masterId: string;
    index: number;
    override: RecurringOverrideTask | null | undefined;
}): ClientRecurringTask {
    if (args.override) {
        return {
            type: "recurring",
            ...args.master,
            ...args.override,
        };
    }
    const startTimeFromRule = calculateDateFromRule(args.master.rule, args.index);
    const schedulingDetails = args.master.isAllDay
        ? {
              scheduleDate: getDateIsoString(plainToLocal(startTimeFromRule)),
          }
        : {
              scheduleTime: startTimeFromRule.toISOString(),
              durationMins: args.master.durationMins,
          };
    return {
        type: "recurring",
        ...args.master,
        masterId: args.masterId,
        index: args.index,
        isDeleted: false,
        schedulingDetails,
    };
}

/**
 * Return the local datetime of the instance.
 */
function getInstanceDate(clientRecurringTask: ClientRecurringTask) {
    invariant(clientRecurringTask.schedulingDetails, "Client recurring task should always have a schedule.");
    if (clientRecurringTask.schedulingDetails.scheduleTime) {
        // new Date() with ISO string produces a local date.
        return new Date(clientRecurringTask.schedulingDetails.scheduleTime);
    }
    if (clientRecurringTask.schedulingDetails.scheduleDate) {
        // new Date("YYYY-MM-DD") produces a plain date. Convert it to local.
        return plainToLocal(new Date(clientRecurringTask.schedulingDetails.scheduleDate));
    }
    throw new Error(
        `Client recurring task ${clientRecurringTask.masterId} ${clientRecurringTask.index} is missing both time and date.`,
    );
}

function filterRecurringTask(args: { task: ClientRecurringTask; filters: CombinedFilter }): boolean {
    if (args.filters.overdue && !isTaskOverdue(args.task.schedulingDetails, args.task.status)) {
        return false;
    }
    if (args.filters.today && !isTaskDueToday(args.task.schedulingDetails)) {
        return false;
    }
    if (args.filters.tagIds.length > 0 && !args.filters.tagIds.includes(args.task.tagId)) {
        return false;
    }
    if (args.filters.projectIds.length > 0 && !args.filters.projectIds.includes(args.task.projectId)) {
        return false;
    }
    if (
        (args.filters.status === "OPEN" && args.task.status === "COMPLETE") ||
        (args.filters.status === "COMPLETE" && args.task.status === "OPEN")
    ) {
        // Explicitly not comparing filters.status to task.status because task.status can be null.
        return false;
    }
    if (args.filters.unscheduled) {
        // Recurring tasks cannot be unscheduled.
        return false;
    }

    return true;
}

type DateSelectionStrategy = (
    selected: ClientRecurringTask[],
    current: ClientRecurringTask,
    filters: CombinedFilter,
) => { selected: ClientRecurringTask[]; stopIteration: boolean };

export const matchingDateStrategy = (localDate: Date): DateSelectionStrategy => {
    const targetPlain = localToPlain(localDate);
    return (selected, current, filters) => {
        const currentPlain = localToPlain(getInstanceDate(current));
        const match = isSamePlainDay(targetPlain, currentPlain) && filterRecurringTask({ task: current, filters });
        if (match) {
            return { selected: [current], stopIteration: true };
        } else {
            return { selected, stopIteration: currentPlain > targetPlain };
        }
    };
};

export const dateRangeStrategy = (startLocal: Date, endLocal: Date): DateSelectionStrategy => {
    const startUnix = startLocal.getTime();
    const endUnix = endLocal.getTime();
    return (selected, current, filters) => {
        const instanceUnix = getInstanceDate(current).getTime();
        const match =
            instanceUnix >= startUnix && instanceUnix <= endUnix && filterRecurringTask({ task: current, filters });
        const stopIteration = instanceUnix > endUnix;
        if (match) {
            return { selected: [...selected, current], stopIteration };
        } else {
            return { selected, stopIteration };
        }
    };
};

export const closestToTodayStrategy = (): DateSelectionStrategy => {
    const todayStart = getPlainTodayStart().getTime();
    return (selected, current, filters) => {
        let bestInstance = selected[0];
        let bestTime = bestInstance ? getInstanceDate(bestInstance).getTime() : null;
        const currentTime = getInstanceDate(current).getTime();
        const stopIteration = currentTime >= todayStart;
        if (!filterRecurringTask({ task: current, filters })) {
            return { stopIteration, selected };
        }

        if (bestTime == null) {
            return { stopIteration, selected: [current] };
        } else {
            if (bestTime < todayStart) {
                // The best instance is still in the past.
                if (currentTime >= todayStart) {
                    // Always prefer the future instance over the past.
                    return { stopIteration, selected: [current] };
                } else if (bestTime < currentTime) {
                    // This instance is still in the past but more recent.
                    return { stopIteration, selected: [current] };
                }
            } else {
                // The best instance is today or after.
                if (currentTime < bestTime) {
                    // This instance is closer to today.
                    return {
                        stopIteration,
                        selected: [current],
                    };
                }
            }
        }
        return { stopIteration, selected };
    };
};

function selectTasksToBeDisplayed(
    master: Identifiable<RecurringMasterTask>,
    overrides: Record<number, RecurringOverrideTask>,
    dateSelectionStrategy: DateSelectionStrategy,
    filters: CombinedFilter,
): ClientRecurringTask[] {
    let ret: ClientRecurringTask[] = [];
    // Iterate over overrides.
    for (const override of Object.values(overrides)) {
        if (override.isDeleted) {
            continue;
        }
        const task = buildClientRecurringTask({
            master: master.document,
            masterId: master.id,
            index: override.index,
            override,
        });
        const selectResult = dateSelectionStrategy(ret, task, filters);
        ret = selectResult.selected;
        // Always iterate all overrides.
    }

    // Search from master instances.
    const rrule = RRule.fromString(master.document.rule);
    rrule.all((instanceDate, instanceIndex) => {
        // Do not count overriden instances.
        const isOverriden = Boolean(overrides[instanceIndex]);
        if (isOverriden) {
            return true;
        }
        const task = buildClientRecurringTask({
            master: master.document,
            masterId: master.id,
            index: instanceIndex,
            override: null,
        });
        const selectResult = dateSelectionStrategy(ret, task, filters);
        ret = selectResult.selected;
        return !selectResult.stopIteration;
    });
    return ret;
}

function assembleTasksFromMastersAndOverrides(
    masters: Record<string, RecurringMasterTask>,
    overrides: Record<string, Record<number, RecurringOverrideTask>>,
    dateSelectionStrategy: DateSelectionStrategy,
    filters: CombinedFilter,
): ClientRecurringTask[] {
    let ret: ClientRecurringTask[][] = [];
    for (const [masterId, master] of Object.entries(masters)) {
        ret.push(
            selectTasksToBeDisplayed(
                { id: masterId, document: master },
                overrides[masterId] ?? {},
                dateSelectionStrategy,
                filters,
            ),
        );
    }
    return ret.flat();
}
